import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api, configureFormat } from '@general/ui-kit'

import { loadBrand, applyAccent } from './brand'

/**
 * The client company profile.
 *
 * Loaded once after sign-in and applied to the whole UI: the accent colour
 * becomes --color-primary and the locale drives every date and amount on screen.
 * That is how one build serves a blue client in Spanish and a green one in
 * Catalan without a rebuild.
 */
export const useCompanyStore = defineStore('company', () => {
  const company = ref(null)
  const loading = ref(false)

  function applyBranding(profile) {
    if (!profile) return

    applyAccent(profile.accentColor)

    configureFormat({ locale: profile.locale, currency: profile.currency })
    if (profile.locale) document.documentElement.lang = profile.locale.split('-')[0]
    // The tab's title is the shell's (App.vue), from the brand: the app's name
    // and the screen's, not the company's legal identity.
  }

  async function load() {
    loading.value = true
    try {
      const { company: profile } = await api.get('/company')
      company.value = profile
      applyBranding(profile)
      return profile
    } catch {
      // A missing profile must not block the app: the defaults are usable.
      return null
    } finally {
      loading.value = false
    }
  }

  async function save(changes) {
    const { company: profile } = await api.patch('/company', changes)
    company.value = profile
    applyBranding(profile)
    // The name, the colour or the logo may have changed the installed app:
    // the favicon and the title follow at once, without a reload.
    loadBrand({ force: true })
    return profile
  }

  return { company, loading, load, save }
})
